import {
  collection,
  doc,
  getDocs,
  addDoc,
  updateDoc,
  deleteDoc,
  query,
  orderBy,
  onSnapshot,
  Timestamp
} from 'firebase/firestore';
import { db } from '../config';
import { TreatmentCategory } from '../../../types';
import { useState, useEffect } from 'react';

const COLLECTION_NAME = 'treatmentCategories';

const toCategory = (doc: any): TreatmentCategory => {
  const data = doc.data();
  return {
    id: doc.id,
    name: data.name || '',
    nameAr: data.nameAr || '',
    description: data.description || '',
    isActive: data.isActive ?? true,
    createdAt: data.createdAt?.toDate() || new Date(),
    updatedAt: data.updatedAt?.toDate() || new Date(),
    createdBy: data.createdBy,
  };
};

export const getTreatmentCategories = async (): Promise<TreatmentCategory[]> => {
  const q = query(collection(db, COLLECTION_NAME), orderBy('name'));
  const snapshot = await getDocs(q);
  return snapshot.docs.map(toCategory);
};

export const subscribeTreatmentCategories = (callback: (categories: TreatmentCategory[]) => void) => {
  const q = query(collection(db, COLLECTION_NAME), orderBy('name'));
  return onSnapshot(q, (snapshot) => {
    const categories = snapshot.docs.map(toCategory);
    callback(categories);
  });
};

export const addTreatmentCategory = async (
  category: Omit<TreatmentCategory, 'id' | 'createdAt' | 'updatedAt'>
): Promise<string> => {
  const docRef = await addDoc(collection(db, COLLECTION_NAME), {
    ...category,
    createdAt: Timestamp.now(),
    updatedAt: Timestamp.now(),
  });
  return docRef.id;
};

export const updateTreatmentCategory = async (
  id: string,
  updates: Partial<Omit<TreatmentCategory, 'id' | 'createdAt'>>
): Promise<void> => {
  const docRef = doc(db, COLLECTION_NAME, id);
  await updateDoc(docRef, {
    ...updates,
    updatedAt: Timestamp.now(),
  });
};

export const deleteTreatmentCategory = async (id: string): Promise<void> => {
  const docRef = doc(db, COLLECTION_NAME, id);
  await deleteDoc(docRef);
};

// React hook for real-time categories
export const useTreatmentCategories = () => {
  const [categories, setCategories] = useState<TreatmentCategory[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const unsubscribe = subscribeTreatmentCategories((newCategories) => {
      setCategories(newCategories);
      setLoading(false);
    });

    return () => unsubscribe();
  }, []);

  return { categories, loading };
};
